import { useEffect, useMemo, useState } from "react";
import { FileDown, Search, Watch } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { HIT_TEXT, POSITIONS, RUNNER_TEXT } from "@/lib/jobs";

const HITS = ["all", "ground", "fly", "bunt"];

export const CustomPlaysSheet = ({ open, onOpenChange, teamId, onPick, version, onExport, onWristband }) => {
  const [plays, setPlays] = useState([]);
  const [q, setQ] = useState("");
  const [hit, setHit] = useState("all");
  const [picked, setPicked] = useState([]);
  const [bandPos, setBandPos] = useState("");
  const toggle = (key) => setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  useEffect(() => { if (open) api.customPlays(teamId).then(setPlays); }, [open, teamId, version]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return plays.filter((p) => (hit === "all" || p.hit_type === hit) &&
      (!t || `${p.explanation} ${p.coach_note || ""} ${p.fielder}`.toLowerCase().includes(t)));
  }, [plays, q, hit]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="custom-plays-sheet">
        <SheetHeader><SheetTitle className="font-heading text-2xl uppercase">Customized plays ({plays.length})</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
            <input data-testid="custom-plays-search" className="field-input w-full !pl-10" placeholder="Search plays, e.g. SS or relay" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="seg-group w-full">
            {HITS.map((h) => (
              <button key={h} data-testid={`custom-filter-${h}`} className={`seg-btn flex-1 !min-w-0 !px-1 ${hit === h ? "seg-btn-active" : ""}`} onClick={() => setHit(h)}>
                {h === "all" ? "All" : HIT_TEXT[h]}
              </button>
            ))}
          </div>
          {onExport && shown.length > 0 && (
            <div className="flex gap-2">
              <button className="btn-primary flex-1 !text-base" data-testid="export-selected-pdf-button" disabled={!picked.length}
                onClick={() => onExport(plays.filter((p) => picked.includes(p.key)))}><FileDown size={18} /> Export {picked.length || ""} selected</button>
              <button className="btn-ghost !text-base" data-testid="export-all-pdf-button" onClick={() => onExport(shown)}>All shown</button>
            </div>
          )}
          {onWristband && shown.length > 0 && (
            <div className="flex gap-2 items-center" data-testid="wristband-controls">
              <select data-testid="wristband-position-select" className="field-input flex-1 min-w-0" value={bandPos} onChange={(e) => setBandPos(e.target.value)}>
                <option value="">Diagram cards (all players)</option>
                {POSITIONS.map((p) => <option key={p} value={p}>{p} wristband (job list)</option>)}
              </select>
              <button className="btn-ghost !text-base" data-testid="wristband-export-button"
                onClick={() => onWristband(picked.length ? plays.filter((p) => picked.includes(p.key)) : shown, bandPos || null)}>
                <Watch size={18} /> Wristband
              </button>
            </div>
          )}
          {shown.length === 0 && <p className="text-sm font-semibold text-[var(--muted)] py-6 text-center" data-testid="custom-plays-empty">No customized plays yet. Tap a play on the field, then Edit.</p>}
          {shown.map((p) => (
            <div key={p.key} className="flex items-stretch gap-2">
            {onExport && (
              <label className="pick-box" data-testid="custom-play-checkbox">
                <input type="checkbox" checked={picked.includes(p.key)} onChange={() => toggle(p.key)} className="w-5 h-5 accent-[var(--accent-strong)]" />
              </label>
            )}
            <button data-testid="custom-play-item" className="custom-item" onClick={() => { onPick(p); onOpenChange(false); }}>
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                <span className="chip">{HIT_TEXT[p.hit_type]} · {p.fielder}</span>
                <span className="chip">{p.outs} out{p.outs === 1 ? "" : "s"}</span>
                <span className="chip">{RUNNER_TEXT[p.runners]}</span>
              </div>
              <p className="text-sm font-semibold leading-snug">{p.explanation}</p>
            </button>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};
